import classNames from 'classnames';
import {PropsWithChildren, useCallback} from 'react';
import {Link, useHistory} from 'react-router-dom';
import {ListPropsType, UnsortedListEntityType}
  from '../../constants/types';
import TableHeaderRow from './TableHeaderRow';
import {useAppSelector} from '../../store/hooks';

type UnsortableTableProps = PropsWithChildren<{
  entity: UnsortedListEntityType;
  content: ListPropsType[];
  emptyTableMsg: string;
}>

export const UnsortableTable = ({
  entity,
  content,
  emptyTableMsg,
  children
}: UnsortableTableProps) => {
  const {permissions: currentUserPermissions} =
    useAppSelector(({permissions}) => permissions);
  const tableData = entity?.['fields'];
  const {rowLink, rowLinkPermission} = entity;
  const history = useHistory();

  const hasPermission = useCallback((permissions) => permissions?.some(
      (permission) => currentUserPermissions.includes(permission)
  ), [currentUserPermissions]);

  const renderRows = () => content
      .map((rowItem, rowIndex: number) => {
        const rowCells = tableData.map(({name, link, valuesMap}) => {
          const rowItemValue = rowItem[name];
          const label = valuesMap ?
            valuesMap[rowItemValue] :
              Array.isArray(rowItemValue) ? rowItemValue.map(({name}) => name).join(', ') :
              rowItemValue;

          const haveRowLinkPermission = rowLinkPermission ?
            hasPermission(rowLinkPermission) : true;

          const tableDataClassName = classNames('cell', {
            'cursor-pointer': haveRowLinkPermission && rowLink,
            'disabled': !haveRowLinkPermission && rowLink
          });

          const handleRowClick = rowLink ?
            () => history.push(rowLink(rowItem.id)) :
            () => {};

          return (
            <td
              key={`${name}-row-${rowIndex}`}
              className={tableDataClassName}
              onClick={handleRowClick}
            >
              {!!link ?
                (<Link
                  to={link(rowItem?.id)}
                  className="link">{label}
                </Link>) :
                label}
            </td>
          );
        });

        return (
          <tr className="row" key={rowItem.id}>
            {rowCells}
            {children ?
              Array.isArray(children) ?
                children[rowIndex] :
                children :
              null}
          </tr>
        );
      });

  return (
    <>
      <table className="table">
        <thead>
          <TableHeaderRow
            tableData={tableData}
            hasIconComponent={!!children}
          />
        </thead>
        <tbody>
          {renderRows()}
        </tbody>
      </table>
      {!content?.length &&
        <span className="empty-table-msg">{emptyTableMsg}</span>}
    </>
  );
};
